import { useState } from 'react';
import { fetchApi } from '../../lib/api';

const STAGES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_review', label: 'In Review', color: 'bg-amber-100 text-amber-700' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-gray-100 text-gray-500' },
  { value: 'passed', label: 'Passed', color: 'bg-red-50 text-red-500' },
  { value: 'licensing', label: 'Licensing', color: 'bg-emerald-100 text-emerald-700' },
];

export default function IdeaDetailDrawer({ idea, onClose, onUpdate }) {
  const [notes, setNotes] = useState(idea.notes || '');
  const [rating, setRating] = useState(idea.internal_rating || 0);
  const [saving, setSaving] = useState(false);
  const [similar, setSimilar] = useState(null);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  const handleStageChange = async (stage) => {
    try {
      await fetchApi(`/api/company/ideas/${idea.id}`, {
        method: 'PUT',
        body: JSON.stringify({ pipeline_stage: stage }),
      });
      onUpdate();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await fetchApi(`/api/company/ideas/${idea.id}`, {
        method: 'PUT',
        body: JSON.stringify({ notes, internal_rating: rating || null }),
      });
      onUpdate();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
    setSaving(false);
  };

  const findSimilar = async () => {
    setLoadingSimilar(true);
    try {
      const res = await fetchApi(`/api/company/ideas/${idea.id}/similar`);
      setSimilar(res.similar_ideas || []);
    } catch (err) {
      setSimilar([]);
    }
    setLoadingSimilar(false);
  };

  const scoreBg = (s) => s >= 80 ? 'bg-emerald-100 text-emerald-700' : s >= 60 ? 'bg-amber-100 text-amber-700' : s >= 40 ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-400';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-black/30 absolute inset-0" />
      <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-glass" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-vault-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-heading text-lg text-vault-navy truncate">{idea.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        <div className="p-6 space-y-6">
          {/* AI Score */}
          {idea.ai_status === 'complete' && (
            <div className="flex items-center gap-4">
              <div className={`px-4 py-2 rounded-xl text-center ${scoreBg(idea.wishlist_score)}`}>
                <div className="text-2xl font-bold">{idea.wishlist_score}</div>
                <div className="text-[10px] font-semibold">Wish List Score</div>
              </div>
              <div className="flex-1">
                <div className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold mb-1 ${
                  idea.ai_recommendation === 'pursue' ? 'bg-emerald-100 text-emerald-700' :
                  idea.ai_recommendation === 'hold' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-500'
                }`}>
                  {idea.ai_recommendation === 'pursue' ? '✓ Pursue' : idea.ai_recommendation === 'hold' ? '⏸ Hold' : '✗ Pass'}
                </div>
                <p className="text-xs text-gray-400">{idea.recommendation_reason}</p>
              </div>
            </div>
          )}

          {/* Details */}
          <div className="space-y-3">
            {idea.description && (
              <div>
                <label className="text-[11px] font-semibold text-gray-400 uppercase">Description</label>
                <p className="text-sm text-vault-navy mt-1 leading-relaxed">{idea.description}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {idea.inventor_name && <div><label className="text-[11px] font-semibold text-gray-400 uppercase">Inventor</label><p className="text-sm text-vault-navy mt-0.5">{idea.inventor_name}</p></div>}
              {idea.inventor_email && <div><label className="text-[11px] font-semibold text-gray-400 uppercase">Email</label><p className="text-sm text-vault-navy mt-0.5">{idea.inventor_email}</p></div>}
              {(idea.ai_category || idea.category) && <div><label className="text-[11px] font-semibold text-gray-400 uppercase">Category</label><p className="text-sm text-vault-navy mt-0.5">{idea.ai_category || idea.category}</p></div>}
              {idea.ai_age_range && <div><label className="text-[11px] font-semibold text-gray-400 uppercase">Age Range</label><p className="text-sm text-vault-navy mt-0.5">{idea.ai_age_range}</p></div>}
              {idea.ai_mechanic && <div><label className="text-[11px] font-semibold text-gray-400 uppercase">Mechanic</label><p className="text-sm text-vault-navy mt-0.5">{idea.ai_mechanic}</p></div>}
              {idea.ai_cost_tier && <div><label className="text-[11px] font-semibold text-gray-400 uppercase">Cost Tier</label><p className="text-sm text-vault-navy mt-0.5 capitalize">{idea.ai_cost_tier}</p></div>}
              {idea.source && <div><label className="text-[11px] font-semibold text-gray-400 uppercase">Source</label><p className="text-sm text-vault-navy mt-0.5">{idea.source}</p></div>}
              {idea.date_received && <div><label className="text-[11px] font-semibold text-gray-400 uppercase">Received</label><p className="text-sm text-vault-navy mt-0.5">{idea.date_received}</p></div>}
            </div>
            {idea.wishlist_reasoning && (
              <div>
                <label className="text-[11px] font-semibold text-gray-400 uppercase">AI Analysis</label>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">{idea.wishlist_reasoning}</p>
              </div>
            )}
          </div>

          {/* Pipeline Stage */}
          <div>
            <label className="text-[11px] font-semibold text-gray-400 uppercase mb-2 block">Pipeline Stage</label>
            <div className="flex flex-wrap gap-1.5">
              {STAGES.map(s => (
                <button
                  key={s.value}
                  onClick={() => handleStageChange(s.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    idea.pipeline_stage === s.value ? s.color + ' ring-2 ring-offset-1 ring-vault-gold/30' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Rating */}
          <div>
            <label className="text-[11px] font-semibold text-gray-400 uppercase mb-2 block">Internal Rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setRating(n === rating ? 0 : n)}
                  className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${
                    n <= rating ? 'bg-vault-gold text-white' : 'bg-gray-100 text-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[11px] font-semibold text-gray-400 uppercase mb-1.5 block">Internal Notes</label>
            <textarea
              className="w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm font-body text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none resize-none"
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add internal notes..."
            />
            <button
              onClick={handleSaveNotes}
              disabled={saving}
              className="mt-2 px-4 py-2 rounded-btn text-xs font-semibold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              {saving ? 'Saving...' : 'Save Notes & Rating'}
            </button>
          </div>

          {/* Similar Ideas */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-semibold text-gray-400 uppercase">Similar Inventor Ideas</label>
              {!similar && (
                <button onClick={findSimilar} disabled={loadingSimilar} className="text-xs font-semibold text-vault-gold hover:underline">
                  {loadingSimilar ? 'Searching...' : 'Find Similar'}
                </button>
              )}
            </div>
            {similar && (
              similar.length === 0 ? (
                <p className="text-xs text-gray-400">No similar ideas found among your connected inventors.</p>
              ) : (
                <div className="space-y-2">
                  {similar.map((s, i) => (
                    <div key={i} className="border border-vault-border rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-vault-navy">{s.title}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${scoreBg(s.similarity_score)}`}>
                          {s.similarity_score}% similar
                        </span>
                      </div>
                      <p className="text-xs text-gray-400">by {s.inventor_name}</p>
                      <p className="text-xs text-gray-500 mt-1">{s.reasoning}</p>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
